import moment from 'moment';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
// @mui
import { Box, Stack, Dialog, Grid, Button, AppBar, Typography, Container, Slide, Card, Alert, TextField, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSettingsContext } from 'src/components/settings/context';
import { isEmpty } from 'lodash';
// store
import { setCurrentTreatmentDataGlobal } from 'src/redux/slices/multiTreatment';
import { updateError } from 'src/redux/slices/user';
import { listAmbiguousAppointmentCandidates, resolveAppointmentOutcomeSelection } from '../../utils/appointmentOutcomeSelection';
import useAppointmentOutcome from '../../hooks/useAppointmentOutcome';
import SimilarLeadWarningPopup from '../../sections/@dashboard/leadDetail/SimilarLeadWarningPopup';
import { dispatch, store, useSelector } from '../../redux/store';
import { updateShowSubjectList } from '../../redux/slices/practiceMessage';
// components
import { Archive } from '../tabs';
import { NormalLoading } from '../loading';
import Iconify from '../Iconify';
import { UnableToContact, FailedAppointment } from '../failedToAttendTabs';
import { beginArchiveLead } from '../../redux/slices/lead/coreThunks';
import {
    clearLead,
    saveNewLeadDetail,
    FailedToAttendAppointment,
    updateFollowUpLead,
    updateCompleteStatus,
    Reschedule,
    tableLead,
} from '../../redux/slices/lead';
import {
    commonDialogHeaderStyle,
    onBoardDialogHeader,
    reminderTitleStyle,
    cancelBoxStyle,
    reminderDialogStyle,
    attendedStackStyle,
    leadFooterButtonsStyle,
    PhoneIconButton,
    footerBoxStyle
} from '../../styles/ScheduleReminder';

const Transition = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

export default function OpenUnAttendedPopup({
    open,
    onClose,
    type = 'lead',
    user,
    inputRef,
    headerButtons,
    handleCloseAndReload,
    onBack,
    archiveContext,
    appointmentSelection,
}) {

    const { followLead } = useSelector((state) => state.lead);
    const { practiceId: reduxPracticeId } = useSelector((state) => state.practiceTreatmentList);
    const practiceId = reduxPracticeId;

    const theme = useTheme();
    const isLight = theme.palette.mode === 'light';

    const settings = useSettingsContext();
    const globalTreatmentId = useSelector((state) => state.multiTreatment?.currentTreatmentDataGlobal?.treatmentData?.id);
    // Lead-detail entry points pass the lead (no treatment_id); fall back to the treatment in focus.
    const selectionRow = useMemo(() => ({ ...user, treatment_id: user?.treatment_id ?? globalTreatmentId }), [user, globalTreatmentId]);

    const [activeTab, setActiveTab] = useState(null);
    const [archiveSubmitting, setArchiveSubmitting] = useState(false);
    const archiveSubmittingRef = useRef(false);
    const archiveSubmitContextRef = useRef({ isTaskBodyViewContext: false });
    const [frozenSelection, setFrozenSelection] = useState(null);
    const [outcomeDate, setOutcomeDate] = useState(null);
    const [outcomeReason, setOutcomeReason] = useState('');
    useEffect(() => {
        if (!open) { setFrozenSelection(null); setActiveTab(null); return; }
        if (!frozenSelection) {
            const resolved = resolveAppointmentOutcomeSelection(practiceId, selectionRow, followLead, appointmentSelection);
            if (resolved) {
                setFrozenSelection(resolved);
                const parsed = moment(resolved.appointment.datetime, ['DD-MM-YYYY HH:mm:ss.SSSSSSZ', 'DD-MM-YYYY HH:mm:ss', moment.ISO_8601], true);
                setOutcomeDate(parsed.isValid() ? parsed.toDate() : null);
            }
        }
    }, [open, practiceId, selectionRow, followLead, appointmentSelection, frozenSelection]);
    // Row did not identify the appointment and the treatment has several actionable
    // ones (e.g. a double-submit): let the user choose instead of guessing.
    const ambiguousAppointments = open && !frozenSelection && !appointmentSelection
        ? listAmbiguousAppointmentCandidates(practiceId, selectionRow, followLead) : [];
    const chooseAppointment = (appointmentId) => {
        const picked = resolveAppointmentOutcomeSelection(practiceId, selectionRow, followLead, { appointmentId });
        if (!picked) return;
        setFrozenSelection(picked);
        const parsed = moment(picked.appointment.datetime, ['DD-MM-YYYY HH:mm:ss.SSSSSSZ', 'DD-MM-YYYY HH:mm:ss', moment.ISO_8601], true);
        setOutcomeDate(parsed.isValid() ? parsed.toDate() : null);
    };
    const describeAppointment = (item) => {
        const parsed = moment(item.datetime, ['DD-MM-YYYY HH:mm:ss.SSSSSSZ', 'DD-MM-YYYY HH:mm:ss', moment.ISO_8601], true);
        return `${parsed.isValid() ? parsed.format('DD MMM YYYY, HH:mm') : 'No date'} - ${String(item.status || 'UNKNOWN').replace(/_/g, ' ')} (#${item.id})`;
    };
    const selected = frozenSelection && resolveAppointmentOutcomeSelection(practiceId, selectionRow, followLead, frozenSelection);
    const booked = ['BOOKED', 'CONSULTATION_BOOKED'].includes(selected?.appointment?.status);
    const controlled = Boolean(!selected || (booked && !selected?.appointment?.dentally) || selected?.calcomDerived);
    const outcome = useAppointmentOutcome(selected, open, () => handleFailedToAttendClose());
    const calcom = outcome.result?.calcom || selected?.appointment?.calcom;
    const unsupportedCalcom = selected?.calcomDerived && (!calcom?.booking_uid || calcom.sync_status === 'unlinked');
    const mutationBlocked = !selected || outcome.busy || outcome.uncertain || Boolean(outcome.result?.cancel_archive)
        || ['pending', 'unknown'].includes(calcom?.sync_status);
    const submitControlledArchive = async payload => {
        await outcome.run('archive', {
            reason: payload.data.archiveReason,
            note: payload.data.archiveNotes || '',
            cancellation_reason: outcomeReason,
            is_open_for_communication: payload.openCommunication.is_open_for_communication,
        });
    };
    const finishOutcome = async () => {
        if (mutationBlocked) return;
        if (activeTab === 'Cancel booking and archive' || activeTab === 'Archive') { await inputRef.current.submit('finish'); return; }
        if (activeTab === 'Unable to contact') { await inputRef.current.submit(''); return; }
        if (activeTab === 'Reschedule') {
            if (!outcomeDate || !Number.isFinite(outcomeDate.getTime()) || outcomeDate <= new Date()) return;
            await outcome.run('reschedule', { datetime: outcomeDate.toISOString(), reason: outcomeReason });
        }
        if (activeTab === 'Cancel booking') await outcome.run('cancel', { reason: outcomeReason });
    };


    const handleClose = async (action) => {
        if (action) {
            onClose();
            dispatch(clearLead());
            dispatch(setCurrentTreatmentDataGlobal(null));
        }
    };

    const handleFailedToAttendClose = () => {
        if (selected && (String(store.getState().practiceTreatmentList?.practiceId) !== String(selected.practiceId)
            || String(store.getState().lead?.followLead?.id) !== String(selected.leadId))) return;
        if (handleCloseAndReload) {
            handleCloseAndReload('finish');
        } else {
            onClose('finish');
            dispatch(clearLead());
            dispatch(setCurrentTreatmentDataGlobal(null));
        }
    };

    const handleNext = async () => {
        if (archiveSubmittingRef.current) return;
        archiveSubmittingRef.current = true;
        setArchiveSubmitting(true);
        let keepSubmittingUntilClose = false;
        try {
            const preSubmitFollowUpLead = store.getState().lead?.followUpLead;
            const isTaskBodyViewContext =
                preSubmitFollowUpLead === 'UnAttended' &&
                typeof handleCloseAndReload === 'function';
            archiveSubmitContextRef.current = { isTaskBodyViewContext };

            dispatch(updateCompleteStatus(false));
            if (selected && activeTab === 'Reschedule') { await inputRef.current.submit('finish'); return; }
            if (activeTab === 'Archive' && !selected) {
                dispatch(updateFollowUpLead('archivedLead'));
            } else {
                dispatch(saveNewLeadDetail({ faildAttended: { activeTab } }));
            }

            await inputRef.current.submit('finish');
            if (!store.getState().lead?.markAsComplete) return;

            if (activeTab === 'Archive') {
                keepSubmittingUntilClose = true;
                return;
            }

            if (activeTab === 'Reschedule') {
                if (isTaskBodyViewContext || selected) {
                    dispatch(updateCompleteStatus(false));
                    if (!followLead?.id) {
                        dispatch(updateError({
                            message: 'Cannot reschedule: lead not identified. Please close and reopen.',
                            success: false,
                        }));
                        return;
                    }
                    dispatch(tableLead(followLead));
                    dispatch(Reschedule(handleFailedToAttendClose, selected));
                }
                return;
            }

            handleFailedToAttendClose();
        } finally {
            if (!keepSubmittingUntilClose) {
                archiveSubmittingRef.current = false;
                setArchiveSubmitting(false);
            }
        }
    };

    const handleArchiveSubmit = async (payload) => {
        if (selected && !selected.calcomDerived) {
            await dispatch(beginArchiveLead({ practiceId: selected.practiceId, leadId: selected.leadId,
                leadTreatmentId: selected.leadTreatmentId, reason: payload.data.archiveReason,
                note: payload.data.archiveNotes || '', isOpenForCommunication: payload.openCommunication.is_open_for_communication,
                source: 'appointment-outcome', handleClose: handleFailedToAttendClose }));
            return { success: true };
        }
        const { isTaskBodyViewContext } = archiveSubmitContextRef.current || {};

        if (!isTaskBodyViewContext) {
            dispatch(saveNewLeadDetail({
                archiveLead: {
                    notes: payload?.notes,
                    openCommunication: payload?.openCommunication,
                    archiveData: payload?.archiveData,
                },
            }));
            dispatch(updateCompleteStatus(true));
            return { success: true };
        }

        const archivePracticeId = archiveContext?.practiceId || reduxPracticeId;
        const archiveLeadId = archiveContext?.leadId || user?.lead_id || followLead?.id || user?.id;
        const archiveLeadTreatmentId = archiveContext?.leadTreatmentId || user?.treatment_id || payload?.notes?.treatment || followLead?.lead_treatments?.[0]?.id || null;

        if (!archivePracticeId || !archiveLeadId) {
            dispatch(updateError({ success: false, message: 'Cannot archive this lead: missing practice or lead context.' }));
            return { success: false };
        }
        if (archiveContext?.practiceId && reduxPracticeId && String(archiveContext.practiceId) !== String(reduxPracticeId)) {
            dispatch(updateError({
                success: false,
                message: 'Cannot archive: active practice does not match the task practice. Please switch practice and retry.',
            }));
            return { success: false };
        }

        try {
            await dispatch(beginArchiveLead({
                leadId: archiveLeadId,
                practiceId: archivePracticeId,
                leadTreatmentId: archiveLeadTreatmentId,
                reason: payload?.data?.archiveReason || payload?.notes?.note || payload?.reason || '',
                note: payload?.data?.archiveNotes || payload?.archiveData?.note || payload?.notes?.additional_notes || payload?.note || '',
                isOpenForCommunication: payload?.openCommunication?.is_open_for_communication ?? payload?.isOpenForCommunication,
                source: archiveContext?.source || 'tasks-v2:fta-sidebar-popup',
                handleClose: handleFailedToAttendClose,
            }));
            return { success: true };
        } catch (error) {
            dispatch(updateError({ success: false, message: error?.message || 'Could not archive lead.', cause: error }));
            return { success: false };
        }
    };

    const failedToAttendClick = () => {
        dispatch(saveNewLeadDetail({ faildAttended: { activeTab } }));
        if (!selected) return;
        dispatch(FailedToAttendAppointment({ lead_treatment: selected.leadTreatmentId }, selected.leadId, selected.appointmentId, practiceId, handleFailedToAttendClose, 'Unable to contact'));
    };

    const failedToAttendFinishSubmit = async () => {
        if (archiveSubmittingRef.current) return;
        archiveSubmittingRef.current = true;
        setArchiveSubmitting(true);
        try {
            await inputRef.current.submit('');
        } finally {
            archiveSubmittingRef.current = false;
            setArchiveSubmitting(false);
        }
    };

    const LEGACY_TABS = [
        {
            value: 'Unable to contact',
            icon: <Iconify icon={'akar-icons:circle-x-fill'} width={22} height={22} />,
            component: <UnableToContact ref={inputRef} type={type} handleNext={handleClose} appointmentSelection={selected} explicitOutcome={Boolean(selected)} completeFlow={handleFailedToAttendClose} />,
        },
        {
            value: 'Reschedule',
            icon: <Iconify icon={'eva:calendar-fill'} width={22} height={22} />,
            component: <FailedAppointment ref={inputRef} type={type} handleNext={handleClose} appointmentSelection={selected} onExplicitSubmit={selected ? (formData) => { if (booked) return dispatch(Reschedule(handleFailedToAttendClose, selected, formData)); const data = { ...formData.appointmentDetail, lead: selected.leadId, lead_treatment: selected.leadTreatmentId }; delete data.id; return outcome.run('rebook', data); } : undefined} />,
        },
        {
            value: 'Archive',
            icon: <Iconify icon={'eva:archive-fill'} width={22} height={22} />,
            component: <Archive ref={inputRef} type={type} handleClose={handleClose} onExplicitArchiveSubmit={handleArchiveSubmit} />,
        }
    ];
    const PROFILE_TABS = controlled ? [
        { value: 'Unable to contact', icon: <Iconify icon="akar-icons:circle-x-fill" />,
          component: <UnableToContact ref={inputRef} type={type} appointmentSelection={selected} explicitOutcome
            completeFlow={() => !mutationBlocked && outcome.run('unable', { lead_treatment: selected.leadTreatmentId })} /> },
        { value: 'Reschedule', icon: <Iconify icon="eva:calendar-fill" />,
          component: <Stack spacing={2}><DateTimePicker label="Appointment date (Required)" format="dd/MM/yyyy HH:mm"
            value={outcomeDate} minDateTime={new Date()} ampm={false} onChange={setOutcomeDate}
            disabled={mutationBlocked || !booked || (selected?.calcomDerived && !calcom?.can_reschedule)}
            slotProps={{ textField: { fullWidth: true } }} />
            <TextField label="Reason for change" value={outcomeReason} onChange={e => setOutcomeReason(e.target.value)} />
            {!booked && <Alert severity="warning">This booking is no longer active. Create a new booking through the existing booking flow.</Alert>}</Stack> },
        { value: 'Cancel booking', icon: <Iconify icon="eva:close-circle-fill" />,
          component: <Alert severity="warning">Cancel this appointment. The lead will remain in the CRM.</Alert> },
        ...(!selected?.calcomDerived ? [{ value: 'Archive', icon: <Iconify icon="eva:archive-fill" />, component: <Archive ref={inputRef} type={type} onExplicitArchiveSubmit={payload => dispatch(beginArchiveLead({ practiceId: selected.practiceId, leadId: selected.leadId, leadTreatmentId: selected.leadTreatmentId, reason: payload.data.archiveReason, note: payload.data.archiveNotes || '', isOpenForCommunication: payload.openCommunication.is_open_for_communication, source: 'appointment-outcome', handleClose: handleFailedToAttendClose }))} /> }] : []),
        ...(selected?.calcomDerived ? [{ value: 'Cancel booking and archive', icon: <Iconify icon="eva:archive-fill" />,
          component: <Archive ref={inputRef} type={type} onExplicitArchiveSubmit={submitControlledArchive} /> }] : []),
    ] : LEGACY_TABS;
    const disabledOutcome = mutationBlocked || (activeTab !== 'Unable to contact' && unsupportedCalcom) || (activeTab === 'Reschedule' && (!booked || (selected?.calcomDerived && !calcom?.can_reschedule)))
        || (activeTab === 'Cancel booking' && (!booked || (selected?.calcomDerived && !calcom?.can_cancel)))
        || (activeTab === 'Cancel booking and archive' && !calcom?.can_cancel_and_archive);

    if (!followLead) {
        return (
            <Dialog fullScreen TransitionComponent={Transition} open={open} onClose={handleClose} sx={reminderDialogStyle(type)}>
                <NormalLoading />
            </Dialog>
        );
    }

    return (
        <Dialog
            fullScreen
            TransitionComponent={Transition}
            open={open}
            onClose={handleClose}
            sx={[reminderDialogStyle(type), { pt: { xs: 0, sm: 0 }, '.MuiDialog-paperFullScreen': { bgcolor: '#F5F5F7', scrollbarGutter: 'stable' } }]}
        >
            <AppBar sx={[headerButtons ? commonDialogHeaderStyle : onBoardDialogHeader, { position: 'sticky !important', top: 0, alignItems: 'center' }]}>
                <Typography
                    sx={reminderTitleStyle}
                    variant="h6"
                    component="div"
                >
                    Unattended appointment for <span style={{ textTransform: 'capitalize' }}>{user.first_name}</span>
                </Typography>
                {headerButtons &&
                    <Box sx={cancelBoxStyle}>
                        <Button
                            color="inherit"
                            variant='outlined'
                            style={{ borderColor: theme.palette.common.white, color: theme.palette.common.white }}
                            onClick={() => { handleClose('close'); }}
                        >
                            Cancel
                        </Button>
                    </Box>
                }
            </AppBar>
            <Stack spacing={0} sx={{ ...attendedStackStyle, pt: { md: '24px', xs: '16px' }, pb: { md: '100px', xs: '100px' } }} onClick={() => { dispatch(updateShowSubjectList(false)); }}>
                <Grid container spacing={1} sx={{ maxWidth: '1088px', margin: 'auto' }}>
                    <Container
                        maxWidth={settings.themeStretch ? false : 'xl'}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{ textAlign: 'center', mb: 3, color: 'text.secondary', fontWeight: 500 }}
                        >
                            {user.treatment_name} appointment unattended — call <span style={{ textTransform: 'capitalize', fontWeight: 600, color: 'inherit' }}>{user.first_name} {user.last_name}</span> to find out why
                        </Typography>
                        <Card sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, borderRadius: '12px', border: 'none', maxWidth: '90vw', boxShadow: 'none', bgcolor: '#ffffff' }}>
                            {onBack && (
                                <Box sx={{ width: '100%', mb: 1 }}>
                                    <Button
                                        variant="text"
                                        onClick={onBack}
                                        startIcon={<Iconify icon="eva:arrow-back-fill" width={18} height={18} />}
                                        sx={{ color: '#637381', textTransform: 'none', fontWeight: 600, fontSize: '13px', px: 1, '&:hover': { bgcolor: 'rgba(99,115,129,0.08)' } }}
                                    >
                                        Back
                                    </Button>
                                </Box>
                            )}
                            <a
                                href={followLead && followLead.lead_contact && followLead.lead_contact.phone || ''}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    textDecoration: 'none',
                                    marginBottom: '10px',
                                    maxWidth: '100%'
                                }}
                            >
                                <PhoneIconButton>
                                    <Iconify style={{ color: theme.palette.common.green }} icon={'bxs:phone'} />
                                </PhoneIconButton>
                                {!isEmpty(followLead?.lead_contact?.phone) && <Typography variant="h1" color={isLight ? 'grey.800' : theme.palette.common.white}
                                    sx={{
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontSize: { lg: '48px', md: '48px', sm: '3.25rem', xs: '1.2rem' },
                                    }}
                                >
                                    {followLead.lead_contact.phone}
                                </Typography>}
                            </a>
                            {type === 'recordUnAttended' && <SimilarLeadWarningPopup type={'DialogPopup'} dialogClose={() => handleClose('close')} />}
                            <Box sx={{ width: '100%', mt: 2 }}>
                                <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.022em', lineHeight: 1.2, textAlign: 'center' }}>
                                    What&apos;s next?
                                </Typography>
                                <Typography sx={{ fontSize: '13px', color: '#6e6e73', mt: 0.5, mb: 2, textAlign: 'center' }}>
                                    Find out why they didn&apos;t show up.
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                        gap: 1.5,
                                        width: '100%',
                                    }}
                                >
                                    {PROFILE_TABS.map((tab) => {
                                        const isActive = activeTab === tab.value;
                                        const OUTCOME_META = {
                                            'Unable to contact': { desc: 'Couldn\'t reach them', color: '#FF5630', bg: 'rgba(255,86,48,0.06)' },
                                            'Reschedule': { desc: booked ? 'Change this appointment' : 'Book a new appointment', color: '#078DEE', bg: 'rgba(7,141,238,0.06)' },
                                            'Archive': { desc: 'Lead is not progressing', color: '#637381', bg: 'rgba(99,115,129,0.08)' },
                                        };
                                        const meta = OUTCOME_META[tab.value] || { desc: '', color: '#078DEE', bg: 'rgba(7,141,238,0.06)' };
                                        return (
                                            <Box
                                                key={tab.value}
                                                onClick={() => { if (!outcome.busy) setActiveTab(tab.value); }}
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: { xs: 0.5, sm: 1 },
                                                    px: { xs: 1, sm: 2 },
                                                    py: { xs: 1.5, sm: 2.5 },
                                                    borderRadius: '12px',
                                                    minHeight: { xs: 90, sm: 140 },
                                                    cursor: 'pointer',
                                                    transition: 'border-color 250ms cubic-bezier(0.32, 0.72, 0, 1), box-shadow 250ms cubic-bezier(0.32, 0.72, 0, 1), transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                                                    background: meta.bg,
                                                    border: isActive ? `1.5px solid ${meta.color}` : '1px solid transparent',
                                                    boxShadow: isActive ? `0 0 0 1px ${meta.color}22` : 'none',
                                                    justifyContent: 'center',
                                                    userSelect: 'none',
                                                    '&:hover': { borderColor: meta.color, boxShadow: `0 0 0 1px ${meta.color}22` },
                                                    '&:active': { transform: 'scale(0.96)' },
                                                }}
                                            >
                                                <Box sx={{
                                                    width: { xs: 36, sm: 48 },
                                                    height: { xs: 36, sm: 48 },
                                                    borderRadius: { xs: '10px', sm: '12px' },
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    background: '#fff',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                                }}>
                                                    <Iconify icon={tab.icon.props.icon} width={{ xs: 18, sm: 24 }} sx={{ color: meta.color }} />
                                                </Box>
                                                <Typography sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: 600, color: '#212B36', whiteSpace: 'nowrap' }}>
                                                    {tab.value}
                                                </Typography>
                                                <Typography sx={{ fontSize: { xs: '10px', sm: '11px' }, color: '#919EAB', fontWeight: 400, textAlign: 'center', lineHeight: 1.3, display: { xs: 'none', sm: 'block' } }}>
                                                    {meta.desc}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                                {!selected && ambiguousAppointments.length > 1 && <Alert severity="info" sx={{ mt: 2 }}>
                                    This treatment has more than one appointment. Choose the one this outcome applies to.
                                    <TextField select fullWidth size="small" label="Appointment" value="" sx={{ mt: 1.5 }}
                                        onChange={(e) => chooseAppointment(e.target.value)} inputProps={{ 'data-testid': 'appointment-chooser' }}>
                                        {ambiguousAppointments.map(item => <MenuItem key={item.id} value={item.id}>{describeAppointment(item)}</MenuItem>)}
                                    </TextField>
                                </Alert>}
                                {controlled && (!selected || unsupportedCalcom) && !(ambiguousAppointments.length > 1) && <Alert severity="warning" sx={{ mt: 2 }}>
                                    {!selected ? 'The selected appointment is unavailable. Close and reload before continuing.' : 'Booking updates are unavailable. Check the Cal.com connection.'}
                                </Alert>}
                                {outcome.message && <Alert severity="info" sx={{ mt: 2 }}>{outcome.message}</Alert>}
                                {(outcome.message || calcom?.cancel_archive) && <Stack direction="row" spacing={1}>
                                    <Button disabled={outcome.busy} onClick={outcome.refresh}>Check current status</Button>
                                    {outcome.canRetryCancel && <Button disabled={outcome.busy} onClick={outcome.retryCancel}>Retry cancellation</Button>}
                                    {outcome.canRetryArchive && <Button disabled={outcome.busy} onClick={outcome.retryArchive}>Retry archive</Button>}
                                </Stack>}
                                {activeTab && (!controlled || selected) && (
                                    <Box sx={{ mt: 3, width: '100%' }}>
                                        {PROFILE_TABS.find(t => t.value === activeTab)?.component}
                                    </Box>
                                )}
                            </Box>
                            {!controlled && activeTab === 'Unable to contact' && user.status === 'FAILED_TO_ATTEND_CONSULTATION' &&
                                <Box sx={footerBoxStyle}>
                                    <Container
                                        maxWidth={settings.themeStretch ? false : 'xl'}
                                        sx={leadFooterButtonsStyle}
                                    >
                                        <Button
                                            color="primary"
                                            variant='contained'
                                            onClick={() => failedToAttendFinishSubmit()}
                                            disabled={!activeTab || archiveSubmitting || outcome.busy || outcome.uncertain}
                                            sx={{
                                                bgcolor: '#00AB55',
                                                color: '#ffffff',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                letterSpacing: '-0.01em',
                                                textTransform: 'none',
                                                borderRadius: '6px',
                                                boxShadow: 'none',
                                                px: 2.25,
                                                py: 0.75,
                                                minWidth: '88px',
                                                '&:hover': { bgcolor: '#02934A', boxShadow: 'none' },
                                                '&:active': { bgcolor: '#017A3D' },
                                                '&.Mui-disabled': { bgcolor: 'rgba(0, 171, 85, 0.35)', color: '#ffffff' },
                                            }}
                                        >
                                            {archiveSubmitting && activeTab === 'Archive' ? 'Archiving...' : 'Finish'}
                                        </Button>
                                    </Container>
                                </Box>
                            }
                            {/* Outcome footer shares the existing dialog layout. */}
                            {/* eslint-disable-next-line no-nested-ternary */}
                            {controlled ? <Box sx={footerBoxStyle}><Container sx={leadFooterButtonsStyle}><Button variant="contained" disabled={!activeTab || disabledOutcome} onClick={finishOutcome}>{outcome.busy ? 'Checking…' : 'Finish'}</Button></Container></Box> : activeTab === 'Unable to contact' ?
                                <>
                                    {user.status === 'CONSULTATION_BOOKED'
                                        ?
                                        <Box sx={footerBoxStyle}>
                                            <Container
                                                maxWidth={settings.themeStretch ? false : 'xl'}
                                                sx={leadFooterButtonsStyle}
                                            >
                                                <Button
                                                    variant='contained'
                                                    onClick={() => failedToAttendClick()}
                                                    disabled={!activeTab || archiveSubmitting || outcome.busy || outcome.uncertain}
                                                    sx={{
                                                bgcolor: '#00AB55',
                                                color: '#ffffff',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                letterSpacing: '-0.01em',
                                                textTransform: 'none',
                                                borderRadius: '6px',
                                                boxShadow: 'none',
                                                px: 2.25,
                                                py: 0.75,
                                                minWidth: '88px',
                                                '&:hover': { bgcolor: '#02934A', boxShadow: 'none' },
                                                '&:active': { bgcolor: '#017A3D' },
                                                '&.Mui-disabled': { bgcolor: 'rgba(0, 171, 85, 0.35)', color: '#ffffff' },
                                            }}
                                                >
                                                    Finish
                                                </Button>
                                            </Container>
                                        </Box>
                                        : null}
                                </>
                                : <Box sx={footerBoxStyle}
                                >
                                    <Container
                                        maxWidth={settings.themeStretch ? false : 'xl'}
                                        sx={leadFooterButtonsStyle}
                                    >
                                        <Button
                                            color="primary"
                                            variant='contained'
                                            onClick={handleNext}
                                            disabled={!activeTab || archiveSubmitting || outcome.busy || outcome.uncertain}
                                            sx={{
                                                bgcolor: '#00AB55',
                                                color: '#ffffff',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                letterSpacing: '-0.01em',
                                                textTransform: 'none',
                                                borderRadius: '6px',
                                                boxShadow: 'none',
                                                px: 2.25,
                                                py: 0.75,
                                                minWidth: '88px',
                                                '&:hover': { bgcolor: '#02934A', boxShadow: 'none' },
                                                '&:active': { bgcolor: '#017A3D' },
                                                '&.Mui-disabled': { bgcolor: 'rgba(0, 171, 85, 0.35)', color: '#ffffff' },
                                            }}
                                        >
                                            Finish
                                        </Button>
                                    </Container>
                                </Box>}
                        </Card>
                    </Container>
                </Grid>
            </Stack>
        </Dialog>
    );
}

OpenUnAttendedPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    type: PropTypes.string,
    user: PropTypes.object,
    inputRef: PropTypes.any,
    headerButtons: PropTypes.bool,
    handleCloseAndReload: PropTypes.any,
    onBack: PropTypes.func,
    appointmentSelection: PropTypes.object,
    archiveContext: PropTypes.shape({
        practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        leadTreatmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        source: PropTypes.string,
    }),
};
